import React from "react"
import PropTypes from "prop-types"
import { ReactComponent as CalendarIcon } from "@ciq/shared/assets/icons/calendar_icon.svg"
import "../../../../css/ZoneUploads.css"
import { WarningsPanel } from "./WarningsPanel"
import Divider from "@ciq/shared/components/divider"

/**
 * Format number with commas for display.
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
function formatNumber (num) {
  return num.toLocaleString("en-US")
}

/**
 * Format date for display.
 * @param {string} isoDate - ISO date string
 * @returns {string} Formatted date (MM/DD/YYYY)
 */
function formatDate (isoDate) {
  const date = new Date(isoDate)
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric"
  })
}

/**
 * @typedef {import("./api").JobResults} JobResults
 */

/**
 * Processing summary panel showing zone data results.
 * Displays institution info, room mapping stats, zone breakdown, and capacity totals.
 *
 * @param {Object} props - Component props
 * @param {JobResults} props.data - Processing summary data
 * @returns {React.ReactElement} The summary panel component
 */
export function ProcessingSummary ({ data }) {
  const { processing_date, room_mapping, zones, capacity } = data

  return (
    <div className="processing-summary">
      <h2 className="processing-summary__title">Processing Summary</h2>

      {/* Header with institution and date */}
      <div className="processing-summary__header">
        <div className="processing-summary__content">
          <div className="processing-summary__header-item">
            <div className="processing-summary__header-item__title">
              <CalendarIcon className="processing-summary__icon"/>
              <span className="processing-summary__stat-label">Processing Date</span>
            </div>
            <span className="processing-summary__header-item__value">{formatDate(processing_date)}</span>
          </div>
        </div>
      </div>

      <Divider/>

      {/* Room Mapping Summary */}
      <div className="processing-summary__section">
        <h3 className="processing-summary__section-title">Room Mapping Summary</h3>
        <div className="processing-summary__grid processing-summary__grid--4col">
          <div className="processing-summary__stat">
            <span
              className="processing-summary__stat-value">{formatNumber(room_mapping.total)}</span>
            <span className="processing-summary__stat-label">Total Rooms</span>
          </div>
          <div className="processing-summary__stat">
            <span
              className="processing-summary__stat-value">{formatNumber(room_mapping.mapped)}</span>
            <span className="processing-summary__stat-label">Mapped</span>
          </div>
          <div className="processing-summary__stat">
            <span
              className={`processing-summary__stat-value ${room_mapping.unmapped > 0 ? "warning" : ""}`}
              data-testid="unmapped-count"
            >
              {formatNumber(room_mapping.unmapped)}
            </span>
            <span className="processing-summary__stat-label">Unmapped</span>
          </div>
          <div className="processing-summary__stat">
            <span className="processing-summary__stat-value">{room_mapping.success_rate}%</span>
            <span className="processing-summary__stat-label">Success Rate</span>
          </div>
        </div>
      </div>

      <Divider/>

      {/* Zones Created */}
      <div className="processing-summary__section">
        <h3 className="processing-summary__section-title">Zones Created</h3>
        <div className="processing-summary__grid processing-summary__grid--4col">
          <div className="processing-summary__stat">
            <span className="processing-summary__stat-value">{formatNumber(zones.total)}</span>
            <span className="processing-summary__stat-label">Total Zones</span>
          </div>
          <div className="processing-summary__stat">
            <span className="processing-summary__stat-value">{formatNumber(zones.building)}</span>
            <span className="processing-summary__stat-label">Building Zones</span>
          </div>
          <div className="processing-summary__stat">
            <span className="processing-summary__stat-value">{formatNumber(zones.floor)}</span>
            <span className="processing-summary__stat-label">Floor Zones</span>
          </div>
          <div className="processing-summary__stat">
            <span className="processing-summary__stat-value">{formatNumber(zones.custom)}</span>
            <span className="processing-summary__stat-label">Custom Zones</span>
          </div>
        </div>
      </div>

      <Divider/>

      {/* Capacity & Area */}
      <div className="processing-summary__section">
        <h3 className="processing-summary__section-title">Capacity & Area</h3>
        <div className="processing-summary__grid processing-summary__grid--2col">
          <div className="processing-summary__stat">
            <span
              className="processing-summary__stat-value">{formatNumber(capacity.square_footage)} sq ft</span>
            <span className="processing-summary__stat-label">Total Square Footage</span>
          </div>
          <div className="processing-summary__stat">
            <span
              className="processing-summary__stat-value">{formatNumber(capacity.capacity)}</span>
            <span className="processing-summary__stat-label">Total Capacity</span>
          </div>
        </div>
      </div>

      <Divider/>
      <WarningsPanel warnings={data.warnings}/>
    </div>
  )
}

// Export utility functions for testing
export { formatNumber, formatDate }

ProcessingSummary.propTypes = {
  data: PropTypes.shape({
    institution: PropTypes.string,
    processing_date: PropTypes.string.isRequired,
    room_mapping: PropTypes.shape({
      total: PropTypes.number.isRequired,
      mapped: PropTypes.number.isRequired,
      unmapped: PropTypes.number.isRequired,
      success_rate: PropTypes.number.isRequired
    }).isRequired,
    zones: PropTypes.shape({
      total: PropTypes.number.isRequired,
      building: PropTypes.number.isRequired,
      floor: PropTypes.number.isRequired,
      custom: PropTypes.number.isRequired
    }).isRequired,
    capacity: PropTypes.shape({
      square_footage: PropTypes.number.isRequired,
      capacity: PropTypes.number.isRequired
    }).isRequired,
    warnings: PropTypes.array
  }).isRequired
}
